import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ABONEMENT_WIZARD_OPEN_EVENT } from './abonementWizardEvents';

const PERIOD_LABELS = {
  1: '1 месяц',
  3: '3 месяца',
};

const TARIFF_TYPE_LABELS = {
  day: 'дневные',
  evening: 'вечерние',
};

const PURCHASE_STATE_KEY = 'palma_abonement_purchase_state';
const PURCHASE_STATE_COOKIE_MAX_AGE = 15 * 60;

function formatPrice(price) {
  return `${Number(price).toLocaleString('ru-RU')} ₽`;
}

function renderPrice(tariff) {
  if (tariff.original_price && tariff.original_price > tariff.price) {
    return (
      <span className="abonement-wizard__price">
        <span className="abonement-wizard__price-old">{formatPrice(tariff.original_price)}</span>
        <span className="abonement-wizard__price-new">{formatPrice(tariff.price)}</span>
      </span>
    );
  }
  return <span>{formatPrice(tariff.price)}</span>;
}

function normalizePhone(phone) {
  const value = (phone || '').trim();
  if (!value) {
    return '';
  }
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('8')) {
    return `+7${digits.slice(1)}`;
  }
  if (digits.length === 11 && digits.startsWith('7')) {
    return `+${digits}`;
  }
  if (digits.length === 10) {
    return `+7${digits}`;
  }
  if (value.startsWith('+') && digits) {
    return `+${digits}`;
  }
  return value;
}

function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  if (meta && meta.content) {
    return meta.content;
  }
  const match = document.cookie.match('(^|;)\\s*csrftoken\\s*=\\s*([^;]+)');
  return match ? decodeURIComponent(match.pop()) : '';
}

function shouldResumePurchase() {
  return new URLSearchParams(window.location.search).get('resume_abonement') === '1';
}

function getResumeUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete('login');
  url.searchParams.set('buy_abonement', '1');
  url.searchParams.set('resume_abonement', '1');
  return url.pathname + url.search;
}

function savePurchaseState({ selectedTariff, email, phone }) {
  if (!selectedTariff) {
    return;
  }
  const state = JSON.stringify({
    tariffId: selectedTariff.id,
    email,
    phone: normalizePhone(phone),
  });
  try {
    sessionStorage.setItem(PURCHASE_STATE_KEY, state);
  } catch (error) {
    // Даже если storage недоступен, пользователь всё равно сможет войти.
  }
  document.cookie = `${PURCHASE_STATE_KEY}=${encodeURIComponent(state)}; path=/; max-age=${PURCHASE_STATE_COOKIE_MAX_AGE}; SameSite=Lax`;
}

function loadPurchaseState() {
  try {
    const rawState = sessionStorage.getItem(PURCHASE_STATE_KEY);
    if (rawState) {
      return JSON.parse(rawState);
    }
  } catch (error) {
    // fallback to cookie below
  }
  const match = document.cookie.match(`(^|;)\\s*${PURCHASE_STATE_KEY}\\s*=\\s*([^;]+)`);
  if (!match) {
    return null;
  }
  try {
    return JSON.parse(decodeURIComponent(match.pop()));
  } catch (error) {
    return null;
  }
}

function clearPurchaseState() {
  try {
    sessionStorage.removeItem(PURCHASE_STATE_KEY);
  } catch (error) {
    // ignore
  }
  document.cookie = `${PURCHASE_STATE_KEY}=; path=/; max-age=0; SameSite=Lax`;
}

function openLogin(nextUrl) {
  if (window.openLoginChoiceModal) {
    window.openLoginChoiceModal(nextUrl);
    return;
  }
  window.location.href = `/accounts/login/?next=${encodeURIComponent(nextUrl)}`;
}

function filterTariffsByRequiredType(tariffs, requiredTariffType) {
  if (!requiredTariffType) {
    return tariffs;
  }
  return tariffs.filter((tariff) => tariff.tariff_type === requiredTariffType);
}

function AbonementWizard({
  initialEmail,
  initialPhone,
  initiallyOpen,
  discountPercent,
  isAuthenticated,
  requiredTariffType,
}) {
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const [step, setStep] = useState(1);
  const [tariffs, setTariffs] = useState([]);
  const [activeRequiredTariffType, setActiveRequiredTariffType] = useState(requiredTariffType || '');
  const [activeDiscount, setActiveDiscount] = useState(null);
  const [trialAbonement, setTrialAbonement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState(null);
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [email, setEmail] = useState(initialEmail || '');
  const [phone, setPhone] = useState(initialPhone || '');
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    const openWizard = (event) => {
      const nextRequiredTariffType = event?.detail?.requiredTariffType || requiredTariffType || '';
      setActiveRequiredTariffType(nextRequiredTariffType);
      setSelectedQuantity(null);
      setSelectedTariff(null);
      setStep(1);
      setError('');
      setIsOpen(true);
    };
    window.addEventListener(ABONEMENT_WIZARD_OPEN_EVENT, openWizard);
    return () => window.removeEventListener(ABONEMENT_WIZARD_OPEN_EVENT, openWizard);
  }, [requiredTariffType]);

  useEffect(() => {
    if (!isOpen || tariffs.length > 0) {
      return;
    }
    setLoading(true);
    axios.get('/api/abonement/tariffs/')
      .then((res) => {
        if (res.data.success) {
          const nextTariffs = res.data.tariffs || [];
          setTariffs(nextTariffs);
          setActiveDiscount(res.data.discount || null);
          setTrialAbonement(res.data.trial_abonement || null);
          if (isAuthenticated && shouldResumePurchase()) {
            const savedState = loadPurchaseState();
            const restoredTariff = savedState
              ? nextTariffs.find((tariff) => Number(tariff.id) === Number(savedState.tariffId))
              : null;
            if (restoredTariff) {
              const restoredEmail = initialEmail || savedState.email || '';
              const restoredPhone = normalizePhone(savedState.phone || initialPhone || '');
              setSelectedQuantity(restoredTariff.training_quantity);
              setSelectedTariff(restoredTariff);
              setEmail(restoredEmail);
              setPhone(restoredPhone);
              setStep(3);
              clearPurchaseState();
              createPayment(restoredTariff, restoredEmail, restoredPhone);
            }
          }
        } else {
          setError('Не удалось загрузить тарифы');
        }
      })
      .catch(() => setError('Не удалось загрузить тарифы'))
      .finally(() => setLoading(false));
  }, [initialEmail, initialPhone, isAuthenticated, isOpen, tariffs.length]);

  const availableTariffs = useMemo(
    () => filterTariffsByRequiredType(tariffs, activeRequiredTariffType),
    [activeRequiredTariffType, tariffs]
  );

  const quantityOptions = useMemo(() => {
    const values = [...new Set(availableTariffs.map((t) => t.training_quantity))];
    return values.sort((a, b) => a - b);
  }, [availableTariffs]);

  const tariffsForQuantity = useMemo(() => {
    if (!selectedQuantity) {
      return [];
    }
    return availableTariffs.filter((t) => t.training_quantity === selectedQuantity);
  }, [availableTariffs, selectedQuantity]);

  const resetWizard = () => {
    setStep(1);
    setSelectedQuantity(null);
    setSelectedTariff(null);
    setError('');
  };

  const openWizard = () => {
    resetWizard();
    setIsOpen(true);
  };

  const closeWizard = () => {
    setIsOpen(false);
    resetWizard();
  };

  const createPayment = (tariff, emailValue, phoneValue) => {
    if (!tariff) {
      return;
    }

    if (!emailValue) {
      setError('Укажите email');
      return;
    }

    setPaying(true);
    setError('');
    const data = new URLSearchParams();
    data.append('tariff_id', tariff.id);
    data.append('email', emailValue);
    data.append('phone', normalizePhone(phoneValue));

    axios.post('/payments/create_abonement/', data, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-CSRFToken': getCsrfToken(),
      },
    })
      .then((res) => {
        if (res.data.success && res.data.payment_url) {
          window.location.href = res.data.payment_url;
        } else {
          setError(res.data.error || 'Ошибка создания платежа');
          setPaying(false);
        }
      })
      .catch(() => {
        setError('Ошибка создания платежа');
        setPaying(false);
      });
  };

  const handlePay = () => {
    if (!selectedTariff) {
      return;
    }

    if (!isAuthenticated) {
      const normalizedPhone = normalizePhone(phone);
      setPhone(normalizedPhone);
      savePurchaseState({ selectedTariff, email, phone: normalizedPhone });
      openLogin(getResumeUrl());
      return;
    }

    const normalizedPhone = normalizePhone(phone);
    setPhone(normalizedPhone);
    createPayment(selectedTariff, email, normalizedPhone);
  };

  return (
    <div className="abonement-wizard">
      <button type="button" className="btn btn-primary abonement-wizard__open-btn" onClick={openWizard}>
        Купить абонемент{discountPercent ? ` −${discountPercent}%` : ''}
      </button>

      {isOpen && (
        <div className="abonement-wizard__overlay" onClick={closeWizard}>
          <div className="abonement-wizard__modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="abonement-wizard__close" onClick={closeWizard}>×</button>
            <h3 className="abonement-wizard__title">Покупка абонемента</h3>

            {trialAbonement?.eligible && (
              <p className="abonement-wizard__discount-banner">
                Скидка {trialAbonement.discount_percent}% на абонемент на 1 занятие
              </p>
            )}

            {activeDiscount && (
              <p className="abonement-wizard__discount-banner">
                Скидка {activeDiscount.discount_percent}% на абонементы до {activeDiscount.ends_on.split('-').reverse().join('.')}
              </p>
            )}

            {loading && <p>Загрузка тарифов...</p>}
            {error && <p className="abonement-wizard__error">{error}</p>}
            {activeRequiredTariffType && (
              <p className="abonement-wizard__note">
                Для этой тренировки подходят только {TARIFF_TYPE_LABELS[activeRequiredTariffType] || ''} абонементы.
              </p>
            )}

            {!loading && step === 1 && quantityOptions.length === 0 && (
              <p>Нет доступных тарифов для этой тренировки.</p>
            )}

            {!loading && step === 1 && quantityOptions.length > 0 && (
              <div>
                <p className="abonement-wizard__subtitle">Шаг 1. Выберите количество тренировок</p>
                <div className="abonement-wizard__options">
                  {quantityOptions.map((qty) => (
                    <button
                      key={qty}
                      type="button"
                      className={`abonement-wizard__option ${selectedQuantity === qty ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedQuantity(qty);
                        setSelectedTariff(null);
                        setStep(2);
                      }}
                    >
                      {qty} {qty === 1 ? 'тренировка' : qty < 5 ? 'тренировки' : 'тренировок'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!loading && step === 2 && (
              <div>
                <p className="abonement-wizard__subtitle">
                  Шаг 2. Выберите тип и срок ({selectedQuantity} трен.)
                </p>
                <div className="abonement-wizard__tariffs">
                  {tariffsForQuantity.map((tariff) => (
                    <button
                      key={tariff.id}
                      type="button"
                      className={`abonement-wizard__tariff ${selectedTariff?.id === tariff.id ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedTariff(tariff);
                        setStep(3);
                      }}
                    >
                      <strong>{tariff.tariff_type_label}</strong>
                      <span>{PERIOD_LABELS[tariff.validity_period] || `${tariff.validity_period} мес.`}</span>
                      {renderPrice(tariff)}
                    </button>
                  ))}
                </div>
                <button type="button" className="btn btn-link" onClick={() => setStep(1)}>Назад</button>
              </div>
            )}

            {!loading && step === 3 && selectedTariff && (
              <div>
                <p className="abonement-wizard__subtitle">Шаг 3. Оформление покупки</p>
                <div className="abonement-wizard__summary">
                  <div>{selectedTariff.name}</div>
                  <div>{renderPrice(selectedTariff)}</div>
                </div>
                <label className="abonement-wizard__field">
                  Email
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email для чека"
                  />
                </label>
                <label className="abonement-wizard__field">
                  Телефон
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+7..."
                  />
                </label>
                <p className="abonement-wizard__note">
                  {isAuthenticated ? (
                    <>
                      Нажимая «Оплатить», вы принимаете{' '}
                      <a href="/offer/" target="_blank" rel="noopener noreferrer">оферту</a>.
                    </>
                  ) : (
                    'Сначала войдите в систему. Мы запомним выбранный абонемент и сразу переведём вас к оплате.'
                  )}
                </p>
                <div className="abonement-wizard__actions">
                  <button type="button" className="btn btn-link" onClick={() => setStep(2)}>Назад</button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={paying}
                    onClick={handlePay}
                  >
                    {isAuthenticated
                      ? (paying ? 'Переход к оплате...' : 'Оплатить')
                      : 'Войти и перейти к оплате'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function mountAbonementWizard() {
  const root = document.getElementById('abonement-wizard-root');
  if (!root || !window.React || !window.ReactDOM) {
    return;
  }

  window.ReactDOM.render(
    React.createElement(AbonementWizard, {
      initialEmail: root.dataset.email || '',
      initialPhone: root.dataset.phone || '',
      initiallyOpen: root.dataset.open === '1',
      discountPercent: root.dataset.discountPercent
        ? Number(root.dataset.discountPercent)
        : null,
      isAuthenticated: root.dataset.authenticated === '1',
      requiredTariffType: root.dataset.requiredTariffType || '',
    }),
    root
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountAbonementWizard);
} else {
  mountAbonementWizard();
}

export default AbonementWizard;
